import { useState, type CSSProperties } from "react";
import { isInvalid, round } from "../calc";
import { MAX_AMOUNT, MIN_POSITIVE } from "../constants";
import { numberInput as copy } from "../content";

interface Props {
  value: number;
  onChange: (n: number) => void;
  decimals: number;
  /** Largest value that can be entered (default 99,999.99). */
  max?: number;
  /** A starting estimate the person hasn't confirmed yet. */
  review?: boolean;
  /** Pass `id` when a visible <label> points at this field, `label` when there isn't one. */
  id?: string;
  label?: string;
  className?: string;
  style?: CSSProperties;
}

const show = (n: number, dp: number) => (n === 0 ? "" : String(round(n, dp)));

/**
 * Number field that keeps the text as typed ("1." stays "1.") and only
 * re-formats when the value changes from outside, e.g. a Mi/Km switch.
 */
export function NumberInput({ value, onChange, decimals, max = MAX_AMOUNT, review, id, className, label, style }: Props) {
  const [text, setText] = useState(() => show(value, decimals));
  const [lastValue, setLastValue] = useState(value);

  // The value can also change from outside the field — a Mi/Km switch converts
  // every figure on the page. React's way to react to that is to adjust state
  // during render rather than in an effect, which avoids painting the stale text
  // first and re-rendering over it.
  if (value !== lastValue) {
    setLastValue(value);
    const typed = parseFloat(text);
    if ((Number.isNaN(typed) ? 0 : typed) !== value) setText(show(value, decimals));
  }

  // `||`, not `??`: an explicit `review={false}` should still fall through to
  // the "nothing entered yet" check.
  const flagged = review || isInvalid(value);
  return (
    <input
      id={id}
      type="number"
      step="any"
      min={MIN_POSITIVE}
      max={max}
      inputMode="decimal"
      className={[className, flagged ? "needs-input" : ""].filter(Boolean).join(" ")}
      style={style}
      value={text}
      aria-label={label}
      aria-invalid={flagged}
      title={flagged ? (review ? copy.reviewTitle : copy.emptyTitle) : undefined}
      onChange={(e) => {
        let raw = e.target.value;
        // Keep to the field's decimal places and its maximum so the value always fits.
        const dot = raw.indexOf(".");
        if (dot >= 0 && raw.length - dot - 1 > decimals) raw = raw.slice(0, dot + 1 + decimals);
        let n = parseFloat(raw);
        if (!Number.isFinite(n) || n < 0) n = 0;
        if (n > max) {
          n = max;
          raw = String(max);
        }
        setText(raw);
        onChange(n);
      }}
    />
  );
}
